import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ArrowLeft, CalendarCheck, CalendarX2, CheckCircle2, Clock, Globe, MapPin, Video } from 'lucide-react';
import { apiPublica, errorDe } from './apiPublica';
import MarcoPublico, { Aviso, Cargando } from './MarcoPublico';
import SelectorHuecos from './SelectorHuecos';
import {
  capital, esUrl, fmtHora, nombreZona, textoDiaLargo, trozosConEnlaces, ymdDe, zonaNavegador,
} from '../../lib/calendario';
import { initials } from '../../lib/format';

/** Página pública de reserva (`/reservar/:slug`), como las páginas de citas de Google o Calendly. */
export default function Reservar() {
  const { slug } = useParams();
  const [pagina, setPagina] = useState(null);
  const [error, setError] = useState('');
  const [zona, setZona] = useState(zonaNavegador());
  const [paso, setPaso] = useState('elegir'); // elegir | datos | listo
  const [hueco, setHueco] = useState(null);
  const [form, setForm] = useState(FORM_VACIO);
  const [recarga, setRecarga] = useState(0);
  const [aviso, setAviso] = useState('');
  const [hecho, setHecho] = useState(null);

  useEffect(() => {
    apiPublica.get(`/reservar/${encodeURIComponent(slug)}`)
      .then(({ data }) => setPagina(data))
      .catch((e) => setError(errorDe(e, 'Esta página de reservas no existe o está pausada')));
  }, [slug]);

  if (error) {
    return <MarcoPublico titulo="Reservar"><Aviso icono={<CalendarX2 className="pub-aviso-ico" />} titulo="No se puede reservar" texto={error} /></MarcoPublico>;
  }
  if (!pagina) return <MarcoPublico titulo="Reservar"><Cargando /></MarcoPublico>;

  return (
    <MarcoPublico titulo={pagina.titulo} ancho="ancho" color={pagina.color}>
      <div className="pub-reserva">
        <aside className="pub-info">
          <div className="pub-anfitrion">
            <span className="pub-avatar">{initials(pagina.anfitrion?.nombre, pagina.anfitrion?.correo)}</span>
            <span>{pagina.anfitrion?.nombre}</span>
          </div>
          <h1 className="pub-titulo" data-testid="pub-titulo">{pagina.titulo}</h1>
          <p className="pub-dato"><Clock />{pagina.duracion} min</p>
          {pagina.videollamada && (
            <p className="pub-dato" data-testid="pub-meet"><Video />Videollamada de Orvian Meet (el enlace llega en la confirmación)</p>
          )}
          {pagina.ubicacion && (
            <p className="pub-dato">{esUrl(pagina.ubicacion) ? <Video /> : <MapPin />}
              {esUrl(pagina.ubicacion) ? 'Videollamada (el enlace llega en la confirmación)' : pagina.ubicacion}
            </p>
          )}
          <p className="pub-dato"><Globe />{nombreZona(zona)}</p>
          {hueco && paso !== 'elegir' && (
            <p className="pub-dato pub-dato--fuerte"><CalendarCheck />{capital(textoDiaLargo(ymdDe(hueco, zona)))}, {fmtHora(hueco, zona)} – {fmtHora(new Date(new Date(hueco).getTime() + pagina.duracion * 60000), zona)}</p>
          )}
          {pagina.descripcion && (
            <p className="pub-descripcion">
              {trozosConEnlaces(pagina.descripcion).map((t, i) => (t.enlace
                ? <a key={i} href={t.enlace} target="_blank" rel="noopener noreferrer nofollow">{t.enlace}</a>
                : <React.Fragment key={i}>{t.texto}</React.Fragment>))}
            </p>
          )}
        </aside>

        <section className="pub-panel">
          {paso === 'elegir' && (
            <>
              <h2 className="pub-subtitulo">Elige día y hora</h2>
              {aviso && <p className="pub-error" role="alert">{aviso}</p>}
              <SelectorHuecos
                slug={slug}
                ventanaDias={pagina.ventana_dias}
                zona={zona}
                onZona={setZona}
                recarga={recarga}
                elegido={hueco}
                color={pagina.color}
                onElegir={(h) => { setHueco(h); setAviso(''); setPaso('datos'); }}
              />
            </>
          )}
          {paso === 'datos' && (
            <FormularioReserva
              pagina={pagina}
              slug={slug}
              hueco={hueco}
              f={form}
              setF={setForm}
              onVolver={() => setPaso('elegir')}
              onOcupado={(msg) => { setAviso(msg); setHueco(null); setRecarga((r) => r + 1); setPaso('elegir'); }}
              onListo={(r) => { setHecho(r); setPaso('listo'); }}
            />
          )}
          {paso === 'listo' && hecho && (
            <div className="pub-listo" data-testid="pub-reserva-lista">
              <CheckCircle2 className="pub-listo-ico" />
              <h2>¡Cita confirmada!</h2>
              <p className="pub-listo-cuando">{capital(textoDiaLargo(ymdDe(hecho.inicio, zona)))}, {fmtHora(hecho.inicio, zona)} – {fmtHora(hecho.fin, zona)}</p>
              <p className="pub-ayuda">{nombreZona(zona)}</p>
              <p>Te enviamos la confirmación por correo con la invitación para tu calendario.</p>
              {hecho.meet_url && (
                <a className="pub-boton" href={hecho.meet_url} target="_blank" rel="noopener noreferrer" data-testid="pub-meet-unirse">
                  <Video className="h-4 w-4" /> Unirse con Orvian Meet
                </a>
              )}
              {hecho.gestionar_url && (
                <a className="pub-boton pub-boton--borde" href={hecho.gestionar_url} data-testid="pub-gestionar">Reprogramar o cancelar</a>
              )}
            </div>
          )}
        </section>
      </div>
    </MarcoPublico>
  );
}

const FORM_VACIO = { nombre: '', correo: '', telefono: '', nota: '', trampa: '', respuestas: {} };

/* El estado del formulario vive en Reservar (K10, auditoría 2026-10-05): si la hora se ocupaba o se
   pulsaba «Cambiar la hora», el formulario se desmontaba y se perdía todo lo escrito. */
function FormularioReserva({ pagina, slug, hueco, f, setF, onVolver, onOcupado, onListo }) {
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState('');
  const set = (c) => setF((x) => ({ ...x, ...c }));

  const enviar = async (e) => {
    e.preventDefault();
    setError('');
    setEnviando(true);
    try {
      const { data } = await apiPublica.post(`/reservar/${encodeURIComponent(slug)}`, {
        inicio: hueco, nombre: f.nombre, correo: f.correo, telefono: f.telefono || undefined,
        respuestas: f.respuestas, nota: f.nota || undefined, trampa: f.trampa,
      });
      onListo(data);
    } catch (err) {
      if (err?.response?.status === 409) onOcupado('Esa hora se acaba de ocupar. Elige otra, por favor.');
      else setError(errorDe(err, 'No se pudo hacer la reserva. Inténtalo de nuevo.'));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <form className="pub-form" onSubmit={enviar} data-testid="pub-formulario">
      <button type="button" className="pub-volver" onClick={onVolver}><ArrowLeft className="h-4 w-4" /> Cambiar la hora</button>
      <h2 className="pub-subtitulo">Tus datos</h2>
      {error && <p className="pub-error" role="alert">{error}</p>}
      <label className="pub-campo">Nombre *
        <input required value={f.nombre} onChange={(e) => set({ nombre: e.target.value })} autoComplete="name" maxLength={120} data-testid="pub-nombre" />
      </label>
      <label className="pub-campo">Correo *
        <input required type="email" value={f.correo} onChange={(e) => set({ correo: e.target.value })} autoComplete="email" data-testid="pub-correo" />
      </label>
      {pagina.pedir_telefono && (
        <label className="pub-campo">Teléfono *
          <input required type="tel" value={f.telefono} onChange={(e) => set({ telefono: e.target.value })} autoComplete="tel" />
        </label>
      )}
      {(pagina.preguntas || []).map((q) => (
        <label key={q.id} className="pub-campo">{q.etiqueta}{q.obligatoria ? ' *' : ''}
          {q.tipo === 'parrafo' ? (
            <textarea rows={3} required={q.obligatoria} value={f.respuestas[q.id] || ''} onChange={(e) => set({ respuestas: { ...f.respuestas, [q.id]: e.target.value } })} />
          ) : (
            <input type={q.tipo === 'telefono' ? 'tel' : 'text'} required={q.obligatoria} value={f.respuestas[q.id] || ''} onChange={(e) => set({ respuestas: { ...f.respuestas, [q.id]: e.target.value } })} />
          )}
        </label>
      ))}
      <label className="pub-campo">¿Algo que debamos saber? (opcional)
        <textarea rows={3} value={f.nota} onChange={(e) => set({ nota: e.target.value })} maxLength={2000} />
      </label>
      {/* Campo trampa: invisible para las personas; un robot lo rellena y la reserva se rechaza */}
      <div className="pub-trampa" aria-hidden="true">
        <label>No rellenes este campo<input tabIndex={-1} autoComplete="off" value={f.trampa} onChange={(e) => set({ trampa: e.target.value })} name="sitio_web" /></label>
      </div>
      <button type="submit" className="pub-boton" disabled={enviando} data-testid="pub-confirmar">{enviando ? 'Reservando…' : 'Confirmar la cita'}</button>
    </form>
  );
}
